import React, { useEffect, useMemo, useState } from 'react';
import { FormLabel } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../store';
import { setParams } from '../reducers/paramsReducer';

// Rating range as a two-thumb slider, matching either player's rating (see GetGamesQuery). The ends
// mean no bound (null), so the full width is "any rating". Refetches on release, not per drag frame.
const MIN = 400;
const MAX = 3000;
const STEP = 25;

export default function GamesFiltersRating(): React.ReactNode {
  const { ratingFrom, ratingTo } = useAppSelector((state) => state.paramsReducer.params);
  const dispatch = useAppDispatch();

  const [from, setFrom] = useState<number>(ratingFrom ?? MIN);
  const [to, setTo] = useState<number>(ratingTo ?? MAX);

  useEffect(() => {
    setFrom(ratingFrom ?? MIN);
  }, [ratingFrom]);
  useEffect(() => {
    setTo(ratingTo ?? MAX);
  }, [ratingTo]);

  // Commit to the filter, folding the extremes back to null so the default range clears the filter.
  const commit = (lo: number, hi: number) => {
    dispatch(
      setParams({
        ratingFrom: lo <= MIN ? null : lo,
        ratingTo: hi >= MAX ? null : hi,
      }),
    );
  };

  // Keep the thumbs from crossing: each stays one step clear of the other.
  const onFrom = (value: number) => setFrom(Math.min(value, to - STEP));
  const onTo = (value: number) => setTo(Math.max(value, from + STEP));

  const pct = (v: number) => ((v - MIN) / (MAX - MIN)) * 100;
  const fillStyle = useMemo(
    () => ({ left: `${pct(from)}%`, right: `${100 - pct(to)}%` }),
    [from, to],
  );

  const label = (v: number, end: 'lo' | 'hi') =>
    (end === 'lo' && v <= MIN) || (end === 'hi' && v >= MAX) ? 'Any' : String(v);

  return (
    <div className="cm-rating-slider">
      <div className="cm-rating-slider-head">
        <FormLabel className="mb-0">Rating</FormLabel>
        <span className="cm-rating-slider-values">
          {label(from, 'lo')} &ndash; {label(to, 'hi')}
        </span>
      </div>
      <div className="cm-rating-slider-track">
        <div className="cm-rating-slider-rail" />
        <div className="cm-rating-slider-fill" style={fillStyle} />
        <input
          type="range"
          className="cm-rating-slider-input cm-rating-slider-from"
          min={MIN}
          max={MAX}
          step={STEP}
          value={from}
          aria-label="Rating from"
          onChange={(e) => onFrom(Number(e.target.value))}
          onMouseUp={() => commit(from, to)}
          onTouchEnd={() => commit(from, to)}
          onKeyUp={() => commit(from, to)}
        />
        <input
          type="range"
          className="cm-rating-slider-input cm-rating-slider-to"
          min={MIN}
          max={MAX}
          step={STEP}
          value={to}
          aria-label="Rating to"
          onChange={(e) => onTo(Number(e.target.value))}
          onMouseUp={() => commit(from, to)}
          onTouchEnd={() => commit(from, to)}
          onKeyUp={() => commit(from, to)}
        />
      </div>
    </div>
  );
}
